import { CopyOutlined } from '@ant-design/icons'
import { useTranslations } from 'next-intl'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import PdMedia from '@/components/pd-media/pd-media'

export interface WarrantyReportInfoCardProps {
  sn: string
  mac?: string
  productName?: string
  productImage?: string
  onResetInput: () => void
}

async function copyToClipboard(value: string): Promise<boolean> {
  if (!value || typeof navigator === 'undefined' || !navigator.clipboard) return false
  try {
    await navigator.clipboard.writeText(value)
    return true
  } catch (error) {
    console.error('copy to clipboard failed', error)
    return false
  }
}

/**
 * 报修信息卡片：展示产品图、SN/MAC、确认提示与「重新输入」。
 * H5：确认文本 → 值+复制 → 重新输入；PC：值+重新输入 → 确认文本。
 */
export function WarrantyReportInfoCard({
  sn,
  mac,
  productName,
  productImage,
  onResetInput,
}: WarrantyReportInfoCardProps) {
  const t = useTranslations()
  async function handleCopy(value: string) {
    const isSuccess = await copyToClipboard(value)
    if (isSuccess) {
      toast.success(t('FaultReportingForm.CopySuccess'))
      return
    }
    toast.error(t('FaultReportingForm.CopyFailed'))
  }

  return (
    <div className={clsx('flex', 'gap-1 laptop:gap-2', 'items-stretch')}>
      {/* 产品图片 */}
      <div
        className={clsx(
          'w-20 laptop:w-24',
          'bg-pdbg',
          'rounded-[2px]',
          'flex-shrink-0',
          'overflow-hidden'
        )}
      >
        {productImage && (
          <PdMedia src={productImage} alt={productName || 'Robot'} mask={false} object="contain" />
        )}
      </div>

      {/* 信息区 — H5: 确认文本→值→重置 / PC: 值+重置→确认文本 */}
      <div
        className={clsx(
          'flex-1',
          'bg-pdbg',
          'rounded-[2px]',
          'px-4',
          'py-3 laptop:py-[23px]',
          'flex',
          'flex-col',
          'justify-between laptop:justify-center',
          'gap-2 laptop:gap-0.5'
        )}
      >
        {/* 确认提示 — H5 置顶，PC 置底 */}
        <p
          className={clsx(
            'order-first laptop:order-last',
            'text-14',
            'text-black/60',
            'leading-[21px] laptop:leading-[22px]'
          )}
        >
          {t('FaultReportingForm.ConfirmSNMatch')}
        </p>

        {/* SN/MAC 值行 + PC 端「重新输入」内联 */}
        <div
          className={clsx(
            'order-2 laptop:order-first',
            'flex',
            'flex-col laptop:flex-row',
            'gap-1 laptop:gap-0',
            'laptop:items-center',
            'laptop:justify-between'
          )}
        >
          <div className={clsx('flex', 'flex-col', 'gap-1 laptop:gap-0')}>
            {!!mac && (
              <div className={clsx('flex', 'flex-col laptop:flex-row', 'gap-0.5 laptop:gap-2', 'laptop:items-center')}>
                <p className={clsx('text-16', 'font-semibold', 'text-black/90', 'leading-6')}>
                  MAC:
                </p>
                <div className={clsx('inline-flex', 'items-center', 'gap-2', 'w-fit')}>
                  <p className={clsx('text-16', 'font-semibold', 'text-black/90', 'leading-6')}>
                    {mac}
                  </p>
                  <button
                    type="button"
                    onClick={() => handleCopy(mac)}
                    className={clsx('text-black/40', 'hover:text-black/60', 'transition-colors')}
                    aria-label="Copy MAC"
                  >
                    <CopyOutlined className={clsx('text-base', 'text-black/40')} />
                  </button>
                </div>
              </div>
            )}
            <div className={clsx('flex', 'flex-col laptop:flex-row', 'gap-0.5 laptop:gap-2', 'laptop:items-center')}>
              <p className={clsx('text-16', 'font-semibold', 'text-black/90', 'leading-6')}>
                SN:
              </p>
              <div className={clsx('inline-flex', 'items-center', 'gap-2', 'w-fit')}>
                <p className={clsx('text-16', 'font-semibold', 'text-black/90', 'leading-6')}>
                  {sn || '-'}
                </p>
                {!!sn && (
                  <button
                    type="button"
                    onClick={() => handleCopy(sn)}
                    className={clsx('text-black/40', 'hover:text-black/60', 'transition-colors')}
                    aria-label="Copy SN"
                  >
                    <CopyOutlined className={clsx('text-base', 'text-black/40')} />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* 「重新输入」— 仅 PC 端内联显示 */}
          <button
            type="button"
            onClick={onResetInput}
            className={clsx(
              'hidden laptop:block',
              'text-14',
              'text-pdblue',
              'hover:opacity-80',
              'transition-opacity',
              'leading-[22px]',
              'flex-shrink-0'
            )}
          >
            {t('FaultReportingForm.ResetInput')}
          </button>
        </div>

        {/* 「重新输入」— 仅 H5 端底部显示 */}
        <button
          type="button"
          onClick={onResetInput}
          className={clsx(
            'order-last laptop:hidden',
            'text-16',
            'text-pdblue',
            'leading-5',
            'text-left',
            'self-start'
          )}
        >
          {t('FaultReportingForm.ResetInput')}
        </button>
      </div>
    </div>
  )
}

export default WarrantyReportInfoCard
